import clsx from "clsx";
import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElements";
import { PARK_TYPE } from "constants/globals";
import { TestIds } from "constants/Tests/TestIds";
import { forwardRef } from "react";
import {
  selectHasInventory,
  selectHasStacks,
} from "store/Selectors/StatusSelectors/contentSelector";
import { selectIsOccupied } from "store/Selectors/StatusSelectors/isOccupiedSelector";
import { selectOperationMode } from "store/Selectors/StatusSelectors/operationModeSelector";
import { selectReservationType } from "store/Selectors/StatusSelectors/reservationTypeSelector";
import { useAppSelector } from "hooks/StoreHooks";

import "../DetailsWindow/DetailsWindow.scss";
import "./Etiquette.scss";

type Props = {
  location: WarehouseElementLocation;
  description: string;
  id: string;
  type: string;
  mainGridLocation: WarehouseElementLocation;
  clickHandler?: (event: React.MouseEvent) => void;
  detailsOpened?: boolean;
};

const Etiquette = forwardRef<HTMLDivElement, Props>(
  (
    {
      location,
      description,
      id,
      type,
      mainGridLocation,
      clickHandler,
      detailsOpened = false,
    },
    ref
  ) => {
    const isOccupied = useAppSelector(selectIsOccupied(id));
    const hasInventory = useAppSelector(selectHasInventory(id));
    const hasStacks = useAppSelector(selectHasStacks(id));
    const reservationType = useAppSelector(selectReservationType(id));
    const operationMode = useAppSelector(selectOperationMode(id));
    const mainGridOffset: WarehouseElementLocation = {
      start: {
        x: mainGridLocation.start.x,
        y: mainGridLocation.start.y,
      },
      end: {
        x: mainGridLocation.end ? mainGridLocation.end.x : 0,
        y: mainGridLocation.end ? mainGridLocation.end.y : 0,
      },
    };

    //Offset nécessaire à la shadowbox
    let shadowBoxOffset = 1;
    if (type == PARK_TYPE) shadowBoxOffset++;

    const startGridColumn =
      location.start.x - mainGridOffset.start.x + shadowBoxOffset;
    const startGridRow =
      location.start.y - mainGridOffset.start.y + shadowBoxOffset;

    const endGridColumn = getGridColumnEnd(
      location,
      mainGridOffset,
      shadowBoxOffset
    );

    const endGridRow = getGridRowEnd(location, mainGridOffset, shadowBoxOffset);
    const isHigherThanLarge =
      endGridRow - startGridRow > endGridColumn - startGridColumn;
    return (
      <div id={"etiquette" + id} style={{ display: "contents" }}>
        <div
          ref={ref}
          id={"etiquettesection" + id}
          className={clsx([
            "etiquette-section " +
              (detailsOpened ? "details-opened " : "") +
              createEtiquetteClassName(
                type,
                isOccupied,
                hasStacks,
                hasInventory,
                reservationType,
                operationMode,
                isHigherThanLarge
              ),
          ])}
          style={{
            gridColumnStart: startGridColumn,
            gridColumnEnd: `${location.end ? "span " + endGridColumn : endGridColumn}`,
            gridRowStart: startGridRow,
            gridRowEnd: `${location.end ? "span " + endGridRow : endGridRow}`,
          }}
          {...(clickHandler && { onClick: clickHandler })}>
          <div className="etiquette etiquette-text-wrapper">
            <div
              className="etiquette etiquette-text "
              data-testid={TestIds.ETIQUETTE}>
              {description}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

Etiquette.displayName = "Etiquette";
export default Etiquette;

function createEtiquetteClassName(
  type: string,
  isOccupied: boolean,
  hasInventory: boolean,
  hasStacks: boolean,
  reservationType: string,
  operationMode: string,
  isHigherThanLarge: boolean
): string {
  return clsx(
    type,
    isOccupied && "OCC",
    hasInventory && "inventory",
    hasStacks && "stacks",
    reservationType && `${"rsvt-" + reservationType}`,
    operationMode && operationMode.length > 1 && `${"OM-" + operationMode}`,
    isHigherThanLarge && "high-element"
  );
}

function getGridColumnEnd(
  location: WarehouseElementLocation,
  mainGridOffset: WarehouseElementLocation,
  shadowBoxOffset: number
): number {
  return location.end
    ? +Math.abs(location.end.x - location.start.x + 1)
    : location.start.x - mainGridOffset.start.x + shadowBoxOffset;
}

function getGridRowEnd(
  location: WarehouseElementLocation,
  mainGridOffset: WarehouseElementLocation,
  shadowBoxOffset: number
): number {
  return location.end
    ? Math.abs(location.end.y - location.start.y + 1)
    : location.start.y - mainGridOffset.start.y + shadowBoxOffset;
}
